import {useMemo} from 'react';
import {BrowseCourse} from '@udemy-v2/browse-course';
import {useI18n} from '@udemy-v2/i18n';
import {TrackableDiscoveryItem} from '@udemy-v2/react-browse-event-tracking';
import {withBundleUnit, FrequentlyBoughtTogether} from '@udemy-v2/react-bundle-unit';
import {useRecommendationsSections, RecommendationItem} from '@udemy-v2/react-recommendations';
import {AddToCartButton} from '@udemy-v2/react-shopping-buttons';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {withPriceEventTrackingContext} from '@/features/course-landing-page/events/with-price-event-tracking-context';

/**
 * Why do we compare section IDs?
 *
 * The "Frequently Bought Together" surface can render for two backend-defined section types
 * that come from the GraphQL recommendations response:
 *  - Instructor Led Bundle (ILB) → section id '756138595'
 *  - Standard Frequently Bought Together (FBT) → section id '105216886'
 *
 * Product requirement (EIP-2198): the "extra discount" badge must appear only for ILB sections,
 * not for standard FBT. Until the API exposes an explicit discriminator (e.g., section.type),
 * we gate the badge by checking the section id returned by the backend.
 *
 * Important:
 *  - These ids reflect the current backend contract. If the contract changes, update the ids
 *    here or replace this check with a future explicit section type once available.
 *  - We compare as strings to avoid numeric/BigInt pitfalls when coercing ids.
 *  - Tests document both ids and the gating behavior in:
 *    features/course-landing-page/components/bundle-unit/frequently-bought-together/__tests__/
 *    frequently-bought-together.react-component.spec.tsx
 */
const INSTRUCTOR_BUNDLE_SECTION_ID = '756138595';
//const FBT_BUNDLE_SECTION_ID = '105216886';

export const InternalFrequentlyBoughtTogether = () => {
    const {data: sections, status} = useRecommendationsSections();
    const {gettext} = useI18n();
    const {featureVariantAssignments} = useFeatures(['clp_fbt_extra_discount_badge']);
    const showExtraDiscountBadgeConfig = featureVariantAssignments.get(
        'clp_fbt_extra_discount_badge',
    )?.configuration?.showExtraDiscountBadge;
    const showExtraDiscountBadge =
        typeof showExtraDiscountBadgeConfig === 'boolean' ? showExtraDiscountBadgeConfig : false;

    const sectionId = sections?.[0]?.id?.toString?.() ?? '';
    const isInstructorBundle = sectionId === INSTRUCTOR_BUNDLE_SECTION_ID;

    const courseRecommendations = useMemo(() => {
        if (status === 'loading' || sections.length < 1) return [];
        const courses: TrackableDiscoveryItem<BrowseCourse>[] = [];
        if ('items' in sections[0]) {
            sections[0].items.forEach((item: RecommendationItem) => {
                if (item && typeof item === 'object' && 'course' in item && item.course) {
                    courses.push({
                        ...(item.course as BrowseCourse),
                        frontendTrackingId: item.frontendTrackingId,
                        trackingId: item.trackingId,
                    });
                }
            });
        }
        return courses;
    }, [sections, status]);

    // Don't render if still loading or if no courses found
    if (status === 'loading' || !courseRecommendations?.length) return null;

    return (
        <FrequentlyBoughtTogether
            title={sections[0]?.title ?? undefined}
            courses={courseRecommendations}
            showExtraDiscountBadge={showExtraDiscountBadge && isInstructorBundle}
            options={{openInNewTab: true}}
            addToCartButton={
                <AddToCartButton
                    buyables={courseRecommendations.map((course) => ({
                        id: Number(course.id),
                        buyableObjectType: 'course',
                        frontendTrackingId: course.frontendTrackingId,
                    }))}
                    addToCartButtonText={gettext('Add all to cart')}
                    forceRedirectToCart={true}
                    buttonStyleProps={{
                        size: 'large',
                    }}
                />
            }
        />
    );
};

const FrequentlyBoughtTogetherWithTracking = withPriceEventTrackingContext(
    InternalFrequentlyBoughtTogether,
    {context: 'clp-bundle'},
);

export const FrequentlyBoughtTogetherWithBundleUnit = withBundleUnit(
    FrequentlyBoughtTogetherWithTracking,
);
